import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarClock,
  Plus,
  Eye,
  Ban,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import {
  paymentPlansApi,
  invoicesApi,
  mattersApi,
  clientsApi,
} from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import ConfirmDialog from "../ui/ConfirmDialog";
import InlineNotification, { NotificationState } from "../ui/InlineNotification";

interface Installment {
  dueAt: string;
  amountCents: number;
  status: string;
  paidAt: string | null;
  paymentId: string | null;
}

interface PaymentPlan {
  _id: string;
  clientId: any;
  matterId: any;
  invoiceId: any;
  status: string;
  totalCents: number;
  currency: string;
  installments: Installment[];
  createdAt: string;
}

const formatCurrency = (cents: number, currency = "CAD") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(cents / 100);

const statusColors: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-600",
};

const installmentStatusColors: Record<string, string> = {
  scheduled: "bg-slate-100 text-slate-600",
  processing: "bg-yellow-100 text-yellow-700",
  paid: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-600",
  cancelled: "bg-slate-100 text-slate-500",
};

const PaymentPlansTab: React.FC = () => {
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [matters, setMatters] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);

  // Filter
  const [filterStatus, setFilterStatus] = useState("");

  // Notification & confirmation
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [confirmState, setConfirmState] = useState<{ open: boolean; planId: string }>({ open: false, planId: "" });

  // Detail
  const [detailPlan, setDetailPlan] = useState<PaymentPlan | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    invoiceId: "",
    totalCents: 0,
    installmentCount: 3,
    scheduleType: "monthly" as string,
  });

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (filterStatus) params.status = filterStatus;

      const res = await paymentPlansApi.list(params);
      setPlans(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch payment plans:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [mRes, iRes] = await Promise.all([
          mattersApi.list({ limit: 200, status: "open" }),
          invoicesApi.list({ status: "issued", limit: 200 }),
        ]);
        setMatters(mRes.data?.data || []);
        setInvoices(iRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load form data:", err);
      }
    };
    loadData();
  }, []);

  const viewPlan = async (id: string) => {
    try {
      const res = await paymentPlansApi.get(id);
      setDetailPlan(res.data);
      setShowDetail(true);
    } catch (err) {
      console.error("Failed to fetch payment plan:", err);
    }
  };

  const handleCancelRequest = (id: string) => {
    setConfirmState({ open: true, planId: id });
  };

  const handleCancelConfirmed = async () => {
    const id = confirmState.planId;
    setConfirmState({ open: false, planId: "" });
    try {
      await paymentPlansApi.cancel(id, "Cancelled by user");
      fetchPlans();
      if (showDetail) {
        const res = await paymentPlansApi.get(id);
        setDetailPlan(res.data);
      }
      setNotification({ type: "success", message: "Payment plan cancelled" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to cancel plan" });
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const invoice = invoices.find((inv: any) => inv._id === createForm.invoiceId);
      if (!invoice) {
        setNotification({ type: "warning", message: "Please select an invoice" });
        setCreating(false);
        return;
      }

      const clientId =
        typeof invoice.clientId === "object" ? invoice.clientId._id : invoice.clientId;
      const matterId =
        typeof invoice.matterId === "object" ? invoice.matterId._id : invoice.matterId;
      const totalCents = invoice.balanceDueCents || invoice.totalCents;

      // Generate installments
      const installmentAmount = Math.floor(totalCents / createForm.installmentCount);
      const remainder = totalCents - installmentAmount * createForm.installmentCount;
      const installments = [];
      const now = new Date();

      for (let i = 0; i < createForm.installmentCount; i++) {
        const dueDate = new Date(now);
        if (createForm.scheduleType === "monthly") {
          dueDate.setMonth(dueDate.getMonth() + i + 1);
        } else if (createForm.scheduleType === "biweekly") {
          dueDate.setDate(dueDate.getDate() + (i + 1) * 14);
        } else if (createForm.scheduleType === "weekly") {
          dueDate.setDate(dueDate.getDate() + (i + 1) * 7);
        }

        installments.push({
          dueAt: dueDate.toISOString(),
          amountCents: installmentAmount + (i === createForm.installmentCount - 1 ? remainder : 0),
        });
      }

      await paymentPlansApi.create({
        invoiceId: createForm.invoiceId,
        clientId,
        matterId,
        totalCents,
        schedule: { type: createForm.scheduleType },
        installments,
      });

      setShowCreateModal(false);
      fetchPlans();
      setNotification({ type: "success", message: "Payment plan created successfully" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to create payment plan" });
    } finally {
      setCreating(false);
    }
  };

  const getPaidCount = (installments: Installment[]) =>
    installments.filter((i) => i.status === "paid").length;

  const getProgress = (installments: Installment[]) => {
    if (installments.length === 0) return 0;
    return (getPaidCount(installments) / installments.length) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <InlineNotification notification={notification} onDismiss={() => setNotification(null)} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title="Cancel Payment Plan"
        message="Are you sure you want to cancel this payment plan? Remaining scheduled installments will not be collected."
        confirmLabel="Cancel Plan"
        variant="danger"
        onConfirm={handleCancelConfirmed}
        onCancel={() => setConfirmState({ open: false, planId: "" })}
      />

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <button
          onClick={() => {
            setCreateForm({
              invoiceId: "",
              totalCents: 0,
              installmentCount: 3,
              scheduleType: "monthly",
            });
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-4 w-4" />
          New Payment Plan
        </button>
        <p className="text-sm text-slate-500">{plans.length} plans</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <SearchableSelect
          options={[
            { value: "active", label: "Active" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
          ]}
          value={filterStatus || null}
          onChange={(val) => setFilterStatus(val || "")}
          placeholder="All Statuses"
          clearable
        />
      </div>

      {/* Plans List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : plans.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <CalendarClock className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No payment plans yet</h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            Break large invoices into manageable installments. Set up weekly, bi-weekly, or monthly payment schedules for your clients.
          </p>
          <button
            onClick={() => {
              setCreateForm({
                invoiceId: "",
                totalCents: 0,
                installmentCount: 3,
                scheduleType: "monthly",
              });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-4 w-4" />
            New Payment Plan
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((plan, i) => (
            <motion.div
              key={plan._id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => viewPlan(plan._id)}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-mono text-slate-500">
                    {typeof plan.invoiceId === "object"
                      ? plan.invoiceId?.invoiceNumber
                      : "Invoice"}
                  </p>
                  <p className="text-lg font-bold text-slate-800">
                    {formatCurrency(plan.totalCents)}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    statusColors[plan.status] || "bg-slate-100 text-slate-600"
                  }`}
                >
                  {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                </span>
              </div>

              <div className="mb-3">
                <p className="text-sm text-slate-600">
                  {typeof plan.clientId === "object" ? plan.clientId?.displayName : "Client"}{" "}
                  {typeof plan.matterId === "object" && `| ${plan.matterId?.title}`}
                </p>
              </div>

              {/* Progress bar */}
              <div className="mb-2">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>
                    {getPaidCount(plan.installments)} of {plan.installments.length} installments
                    paid
                  </span>
                  <span>{Math.round(getProgress(plan.installments))}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${getProgress(plan.installments)}%` }}
                    className="h-2 rounded-full"
                    style={{ backgroundColor: "var(--color-accent)" }}
                  />
                </div>
              </div>

              {/* Next due */}
              {plan.status === "active" && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-2">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    Next:{" "}
                    {(() => {
                      const next = plan.installments.find((i) => i.status === "scheduled");
                      return next
                        ? `${formatCurrency(next.amountCents)} due ${new Date(
                            next.dueAt
                          ).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : "All scheduled";
                    })()}
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Plan Detail Drawer */}
      <AnimatePresence>
        {showDetail && detailPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex justify-end"
            onClick={() => setShowDetail(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Payment Plan</h2>
                    <span
                      className={`mt-1 inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[detailPlan.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {detailPlan.status.charAt(0).toUpperCase() + detailPlan.status.slice(1)}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowDetail(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="text-lg font-bold text-slate-800">
                      {formatCurrency(detailPlan.totalCents)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Invoice</p>
                    <p className="text-sm font-medium text-slate-800">
                      {typeof detailPlan.invoiceId === "object"
                        ? detailPlan.invoiceId?.invoiceNumber
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="text-sm font-medium text-slate-800">
                      {typeof detailPlan.clientId === "object"
                        ? detailPlan.clientId?.displayName
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Matter</p>
                    <p className="text-sm font-medium text-slate-800">
                      {typeof detailPlan.matterId === "object"
                        ? detailPlan.matterId?.title
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                    <span>
                      {getPaidCount(detailPlan.installments)} of{" "}
                      {detailPlan.installments.length} installments paid
                    </span>
                    <span>{Math.round(getProgress(detailPlan.installments))}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${getProgress(detailPlan.installments)}%` }}
                      className="h-3 rounded-full"
                      style={{ backgroundColor: "var(--color-accent)" }}
                    />
                  </div>
                </div>

                {/* Installment Schedule */}
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Installment Schedule</h3>
                <div className="space-y-2 mb-6">
                  {detailPlan.installments.map((inst, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-slate-50 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-1.5 rounded-lg ${
                            inst.status === "paid"
                              ? "bg-emerald-100"
                              : inst.status === "failed"
                              ? "bg-red-100"
                              : "bg-slate-200"
                          }`}
                        >
                          {inst.status === "paid" ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                          ) : inst.status === "failed" ? (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">
                            Installment {i + 1}
                          </p>
                          <p className="text-xs text-slate-500">
                            Due{" "}
                            {new Date(inst.dueAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-800">
                          {formatCurrency(inst.amountCents)}
                        </p>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            installmentStatusColors[inst.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {inst.status.charAt(0).toUpperCase() + inst.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                {detailPlan.status === "active" && (
                  <button
                    onClick={() => handleCancelRequest(detailPlan._id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors"
                  >
                    <Ban className="h-4 w-4" />
                    Cancel Plan
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Payment Plan Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Payment Plan"
        icon={CalendarClock}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="createPlanForm"
              disabled={creating || !createForm.invoiceId}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {creating ? "Creating..." : "Create Plan"}
            </button>
          </div>
        }
      >
        <form id="createPlanForm" onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Invoice *
            </label>
            <SearchableSelect
              options={invoices.map((inv: any) => ({
                value: inv._id,
                label: `${inv.invoiceNumber}  -  ${formatCurrency(inv.balanceDueCents || inv.totalCents)} balance`,
              }))}
              value={createForm.invoiceId || null}
              onChange={(val) => setCreateForm({ ...createForm, invoiceId: val || "" })}
              placeholder="Select invoice"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Number of Installments *
            </label>
            <input
              type="number"
              required
              min="2"
              max="24"
              value={createForm.installmentCount}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  installmentCount: parseInt(e.target.value) || 3,
                })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Schedule *
            </label>
            <SearchableSelect
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "biweekly", label: "Bi-weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
              value={createForm.scheduleType}
              onChange={(val) => setCreateForm({ ...createForm, scheduleType: val || "monthly" })}
              placeholder="Select schedule"
            />
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default PaymentPlansTab;
